/**
 * Die Sprachen der Fahrer-App (S5.6) — die eine Quelle für Sprachcode,
 * Eigenbezeichnung und Erkennungs-Tag, gemeinsam genutzt von Frontend und
 * Server.
 *
 * Die Liste ist die aus SESSIONS.md S5.6: Deutsch, Polnisch, Rumänisch,
 * Türkisch, Bulgarisch, Ukrainisch, Englisch. **Kein Arabisch** — ein
 * RTL-Layout ist nicht im MVP, und eine Sprache anzubieten, deren Leserichtung
 * die Oberfläche nicht kann, wäre schlechter als sie wegzulassen.
 *
 * **Polnisch steht direkt hinter Deutsch**, weil es die Betriebssprache des
 * Piloten ist und damit die einzige, die vor dem ersten echten Tag tatsächlich
 * funktionieren muss.
 *
 * Drei Felder, drei Verwendungen:
 *
 *  - `code` — was in `tenant_members.language` steht und was `translations`
 *    als `target_lang` schlüsselt. Zwei Buchstaben, ohne Region: eine Region
 *    unterschiede pl-PL von pl-DE und übersetzte beides doppelt.
 *  - `name` — die **Eigenbezeichnung**. Wer die Oberfläche gerade nicht lesen
 *    kann, sucht in der Sprachliste nach dem Wort, das er kennt; „Polnisch"
 *    hilft ihm dabei nicht.
 *  - `tag` — BCP-47 für die Spracherkennung (S5.4). Ohne Region erkennt sie
 *    zwar meist trotzdem, aber schlechter.
 */
export const APP_LANGUAGES = [
  { code: "de", name: "Deutsch", tag: "de-DE" },
  { code: "pl", name: "Polski", tag: "pl-PL" },
  { code: "ro", name: "Română", tag: "ro-RO" },
  { code: "tr", name: "Türkçe", tag: "tr-TR" },
  { code: "bg", name: "Български", tag: "bg-BG" },
  { code: "uk", name: "Українська", tag: "uk-UA" },
  { code: "en", name: "English", tag: "en-GB" }
];

export const APP_LANGUAGE_CODES = APP_LANGUAGES.map((sprache) => sprache.code);

/** Die Quellsprache dieser Anwendung: die Disponentin tippt Deutsch, das Büro liest Deutsch. */
export const DEFAULT_LANGUAGE = "de";

/**
 * Ob ein Code einer der angebotenen ist. Bewusst streng: `tenant_members.language`
 * steuert die Vorwärmung (server/translationScheduler.js), und ein Tippfehler
 * darin ließe für eine Sprache übersetzen, die niemand anzeigt — bezahlt, aber
 * nie gelesen.
 */
export const isAppLanguage = (code) => APP_LANGUAGE_CODES.includes(String(code ?? "").trim().toLowerCase());

/**
 * Das BCP-47-Tag zu einem Code, oder `undefined`. Die Spracherkennung (S5.4) und
 * die Zahlen- und Datumsformate der Fahreransicht hängen daran.
 *
 * Stand zweimal da — in `i18n/index.ts` mit Rückfall auf den Code, in `speech.ts`
 * ohne (Befund G5). Hier ohne Rückfall, weil die beiden Aufrufstellen ihn
 * unterschiedlich brauchen: ein `Intl`-Konstruktor nimmt auch einen nackten Code,
 * die Spracherkennung soll einen unbekannten unverändert durchlassen.
 */
export const sprachTag = (code) => APP_LANGUAGES.find((sprache) => sprache.code === code)?.tag;

/**
 * Ist das die Sprache, in der die Texte schon dastehen? Dann gibt es nichts zu
 * übersetzen und nichts nachzuschlagen.
 *
 * Stand in `server/ai/translate.js` und war von dort aus zu holen — auch von
 * `server/routes/memberProfile.js`, das eine reine Sprachfrage an der
 * Modellgrenze stellte (Befund G5). Die Antwort hängt an `DEFAULT_LANGUAGE` und
 * gehört daneben.
 */
export const istQuellsprache = (sprache) => String(sprache ?? "").trim().toLowerCase() === DEFAULT_LANGUAGE;
